import { FC, useCallback } from "react";
import { SettingPageWrapper } from "@connectedComponents";
import { ColorsEnum, NavigationPathsEnum } from "@utilities/enums";
import { useControllers } from "./useController";
import { formatPrice } from "@utilities/functions";
import { Icon, Modal, MonthlyRevenueProgression } from "@components";

export const SubscriptionSettings: FC = () => {

    const {
        plans, selectedOptions,
        modules, InvoiceHistory,
        currentPlan, disableModulesUpdatingButton,
        organisationMontlyRevenue,
        total, moduleInfo,
        handleSelect,
        setModuleInfo,
    } = useControllers();

    const renderPlanCard = useCallback(() => (
        <div className="bg-white p-6 rounded-xl shadow border border-grey-100">
            <h2 className="text-xl font-bold text-secondary mb-4">🗂️ Évoluez avec le bon plan</h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {
                    plans.map((plan, index) => {
                        const isCurrent = plan.label === currentPlan?.label;
                        return (
                            <div
                                key={index}
                                className={`rounded-xl p-5 shadow transition duration-300 border-2 ${
                                    isCurrent
                                    ? "border-primary bg-blue-100"
                                    : "border-gray-200 bg-white hover:shadow-md"
                                }`}
                            >
                            <div className="flex justify-between items-center mb-2">
                                <h3 className="text-lg font-semibold text-gray-800">{plan.label}</h3>
                                {isCurrent && (
                                    <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full font-medium">
                                        Actif
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-gray-600">
                                Taux appliqué :{" "}
                                <span className="font-semibold text-gray-800">
                                    {(plan.rate * 100).toFixed(1)}%
                                </span>
                            </p>
                            </div>
                        );
                    })
                }
            </div>
        </div>
    ), [plans, currentPlan]);

    const renderModuleSelector = useCallback(()=> (
        <div className="bg-white p-6 rounded-xl shadow border border-grey-100 space-y-6">
            <h2 className="text-xl font-bold text-secondary mb-4">🛒 Modules personnalisables</h2>

            <div className="mt-6 border rounded-lg bg-gray-50">
                <div
                    className="w-full text-left px-4 py-3 flex justify-between items-center bg-white rounded-t-lg"
                >
                    <span className="text-sm font-medium text-gray-800">
                        Total modules sélectionnés
                    </span>
                    <span className="text-lg font-bold text-secondary">
                        <span className="text-sm font-medium text-gray-800">Total à payé :</span> {total.toLocaleString()} FCFA
                    </span>
                </div>

                <div className="flex flex-wrap gap-4 p-4">
                    {
                        selectedOptions.map((mod, i)=> (
                            <div
                                key={i}
                                className="border border-blue-100 px-4 py-3 rounded-lg bg-white shadow-sm min-w-[160px]"
                            >
                                <p className="text-sm font-semibold text-gray-800">{mod.name}</p>
                                <p className="text-xs text-gray-500 mb-1 italic">Option : {mod.option}</p>
                                <p className="text-sm font-bold text-secondary">
                                    {formatPrice(mod.price)}
                                </p>
                            </div> 
                        ))
                    }
                </div>

                <div className="flex justify-end p-4">
                    <button 
                        className="btn btn-success"
                        disabled={disableModulesUpdatingButton}
                    >
                        Mèttre à jour les modules
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {modules.map(module => (
                <div key={module.name} className="border rounded-lg p-4 relative">
                    <h4 className="font-semibold mb-3 text-primary">{module.name}</h4>
                    <div className="space-y-2">
                    {module.options.map((option, idx) => (
                        <label key={idx} className="flex items-center gap-2">
                        <input
                            type="radio"
                            name={module.name}
                            value={option.label}
                            checked={selectedOptions.some(opt => opt.name === module.name && opt.option === option.label)}
                            onChange={() => handleSelect(module.name, option)}
                            className="radio radio-sm radio-primary"
                        />
                        <span className="text-sm">{option.label} ({option.price.toLocaleString()} FCFA)</span>
                        </label>
                    ))}
                    </div>

                    <div 
                        className="absolute top-2 right-2 z-10 cursor-pointer"
                        onClick={()=> setModuleInfo(module.infos)}
                    >
                        <Icon 
                            iconName="InfoIcon" 
                            size={20} color={ColorsEnum.INFO}
                            onPress={() => setModuleInfo(module.infos)}
                        />
                    </div>
                </div>
                ))}
            </div>
        </div>
    ), [
        total, selectedOptions, setModuleInfo,
        modules, disableModulesUpdatingButton, handleSelect,
    ]);

    const renderHistory = useCallback(()=> {
        if(InvoiceHistory.length <= 0) {
            return (
                <div className="bg-white p-6 rounded-xl shadow border border-grey-100">
                    <h2 className="text-xl text-secondary font-bold mb-4">🧾 Historique des paiements</h2>
                    <p className="text-gray-600">Aucune facture trouvée.</p>
                </div>
            );
        }

        return (
            <div className="bg-white p-6 rounded-xl shadow border border-grey-100">
                <h2 className="text-xl text-secondary font-bold mb-4">🧾 Historique des paiements</h2>

                <table className="table w-full text-sm">
                    <thead>
                    <tr className="text-left text-gray-600">
                        <th className="py-2">Date</th>
                        <th className="py-2">Montant à payer</th>
                        <th className="py-2">Facture</th>
                    </tr>
                    </thead>
                    <tbody>
                    {InvoiceHistory.map((invoice, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 border-b">
                        <td className="py-2">{`${invoice.month}/${invoice.year}`}</td>
                        <td className="py-2 text-gray-800 font-medium">{formatPrice(invoice.totalDue)}</td>
                        <td className="py-2">
                            <button
                                className="btn btn-sm btn-outline btn-primary"
                                onClick={() => {}}
                            >
                                Télécharger
                            </button>
                        </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        )
    }, [InvoiceHistory]);

    return (
        <SettingPageWrapper activeTab={NavigationPathsEnum.SETTINGS_SUBSCRIPTION}>
            <div className="p-4 space-y-8">
                <MonthlyRevenueProgression 
                    plans={plans}
                    monthlyRevenue={organisationMontlyRevenue?.totalRevenue || 0} 
                    nextInvoiceDate={organisationMontlyRevenue?.nextInvoiceDate || new Date()}    
                />

                {renderPlanCard()}

                {renderModuleSelector()}

                {renderHistory()}
            </div>

            <Modal
                isOpen={!!moduleInfo}
                onClose={() => setModuleInfo("")}
                title="Informations sur le module"
                customClassName="max-w-2xl"
            >
                <div className="p-4">
                    <p className="text-gray-700">{moduleInfo}</p>
                </div>
            </Modal>
        </SettingPageWrapper>
    );
};
